import { collabDatabaseBatch } from '@databases/collab-state-manager/batch';
import { getService } from '@databases/service-container';
import { DatabaseFieldType } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  applyPageLabels,
  getContentLabels,
} from '@databases/utils/database/field-types/pageLabels';
import {
  applyPageStatus,
  getReferencedStatusContentRefs,
} from '@databases/utils/database/field-types/pageStatus';
import {
  createEntry,
  createField,
  getDatabaseEntryOptional,
  getExistingFieldNames,
} from '@databases/utils/database/utils';
import { getHiddenFields } from '@databases/utils/database/view/view';
import { getUniqueFieldName } from '@databases/utils/field-name';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch } from '@databases/utils/redux/redux';
import { generateId } from '@databases/utils/utils';
import { type AnyAction } from 'redux';
import { type ThunkAction, type ThunkDispatch } from 'redux-thunk';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import { type DefaultRootState } from '../..';
import { addChangeToDraft } from '../changesets/actions';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity as we migrate away from cellSelections slice dependency.
 */
const insertEntriesInternal = (
  contentId: string,
  viewSettings: DatabaseViewSettings | undefined,
  insertBefore: boolean,
  selectedEntryIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const database = selectDatabaseByContentIdRequired(getState(), {
    contentId,
  });
  const entry = createEntry(database, viewSettings);

  if (selectedEntryIds.length === 0) {
    return;
  }

  const aboveOrBelowSelectedEntry = insertBefore
    ? selectedEntryIds[0]
    : selectedEntryIds[selectedEntryIds.length - 1];

  const index = database.entries.indexOf(aboveOrBelowSelectedEntry);
  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.ADD_ENTRY,
      entry,
      index: insertBefore ? index : index + 1,
    })
  );
  getService('SelectionsActions').deselectAll();
};

/**
 * @deprecated Use `doInsertEntriesWithIds` instead to avoid dependency on cellSelections slice
 */
export const doInsertEntries =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    insertBefore: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    insertEntriesInternal(
      contentId,
      viewSettings,
      insertBefore,
      reader.getEntryIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doInsertEntriesWithIds =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    insertBefore: boolean,
    selectedEntryIds: string[]
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    insertEntriesInternal(
      contentId,
      viewSettings,
      insertBefore,
      selectedEntryIds,
      dispatch,
      getState
    );
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity as we migrate away from cellSelections slice dependency.
 */
const duplicateEntriesInternal = (
  contentId: string,
  selectedEntryIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>
) => {
  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLONE_ENTRIES,
      entryIds: Object.fromEntries(
        selectedEntryIds.map((entryId) => [entryId, generateId()])
      ),
    })
  );
};

/**
 * @deprecated Use `doDuplicateEntireEntriesWithIds` instead to avoid dependency on cellSelections slice
 */
export const doDuplicateEntireEntries =
  (contentId: string): Dispatcher<void> =>
  (dispatch) => {
    const reader = getService('SelectionsReader');
    duplicateEntriesInternal(contentId, reader.getSelectedEntryIds(), dispatch);
  };

export const doDuplicateEntireEntriesWithIds =
  (contentId: string, selectedEntryIds: string[]): Dispatcher<void> =>
  (dispatch) => {
    duplicateEntriesInternal(contentId, selectedEntryIds, dispatch);
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity as we migrate away from cellSelections slice dependency.
 */
const clearEntriesInternal = (
  contentId: string,
  selectedEntryIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>
) => {
  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLEAR_ENTRIES,
      entryIds: selectedEntryIds,
    })
  );
};

/**
 * @deprecated Use `doClearEntireEntriesWithIds` instead to avoid dependency on cellSelections slice
 */
export const doClearEntireEntries =
  (contentId: string): Dispatcher<void> =>
  (dispatch) => {
    const reader = getService('SelectionsReader');
    clearEntriesInternal(contentId, reader.getSelectedEntryIds(), dispatch);
  };

export const doClearEntireEntriesWithIds =
  (contentId: string, selectedEntryIds: string[]): Dispatcher<void> =>
  (dispatch) => {
    clearEntriesInternal(contentId, selectedEntryIds, dispatch);
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity as we migrate away from cellSelections slice dependency.
 */
const deleteEntriesInternal = (
  contentId: string,
  selectedEntryIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  batch(() => {
    collabDatabaseBatch({ dispatch, getState }, contentId, () => {
      selectedEntryIds.forEach((entryId) => {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.DELETE_ENTRY,
            entryId: entryId,
          })
        );
      });
    });
  });
};

/**
 * @deprecated Use `doDeleteEntriesWithIds` instead to avoid dependency on cellSelections slice
 */
export const doDeleteEntries =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    deleteEntriesInternal(
      contentId,
      reader.getEntryIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doDeleteEntriesWithIds =
  (contentId: string, selectedEntryIds: string[]): Dispatcher<void> =>
  (dispatch, getState) => {
    deleteEntriesInternal(contentId, selectedEntryIds, dispatch, getState);
  };

export const doSelectNewEntries =
  (contentId: string, callback: () => Promise<void> | void): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });

    await callback();

    const newDatabase = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });

    const newEntryIds = newDatabase.entries.filter(
      (id) => !database.entries.includes(id)
    );

    if (newEntryIds.length > 0) {
      batch(() => {
        getService('SelectionsActions').selectEntries(newEntryIds);
      });
    }
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity as we migrate away from cellSelections slice dependency.
 */
const moveEntriesInternal = (
  contentId: string,
  moveUp: boolean,
  selectedEntryIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const databaseDraft = selectDatabaseByContentIdRequired(getState(), {
    contentId,
  });
  const firstOrLastEntryId = moveUp
    ? selectedEntryIds[0]
    : selectedEntryIds[selectedEntryIds.length - 1];
  const currentIndexInEntryIds = databaseDraft.entries.indexOf(firstOrLastEntryId);
  batch(() => {
    collabDatabaseBatch({ dispatch, getState }, contentId, () => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.REORDER_ENTRIES,
          entryIds: selectedEntryIds,
          newIndex: moveUp ? currentIndexInEntryIds - 1 : currentIndexInEntryIds + 2,
        })
      );
    });
  });
};

/**
 * @deprecated Use `doMoveEntriesWithIds` instead to avoid dependency on cellSelections slice
 */
export const doMoveEntries =
  (contentId: string, moveUp: boolean): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    moveEntriesInternal(
      contentId,
      moveUp,
      reader.getEntryIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doMoveEntriesWithIds =
  (contentId: string, moveUp: boolean, selectedEntryIds: string[]): Dispatcher<void> =>
  (dispatch, getState) => {
    moveEntriesInternal(contentId, moveUp, selectedEntryIds, dispatch, getState);
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity as we migrate away from cellSelections slice dependency.
 */
const insertFieldsInternal = (
  contentId: string,
  currentViewId: string,
  insertBefore: boolean,
  fieldName: string,
  selectedFieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const database = selectDatabaseByContentIdRequired(getState(), {
    contentId,
  });
  const newField = createField(DatabaseFieldType.TEXT);
  const uniqueFieldName = getUniqueFieldName(
    fieldName,
    getExistingFieldNames(database, newField)
  );

  if (selectedFieldIds.length === 0) {
    return;
  }

  const beforeOrAfterSelectedField = insertBefore
    ? selectedFieldIds[0]
    : selectedFieldIds[selectedFieldIds.length - 1];

  const index = database.fields.indexOf(beforeOrAfterSelectedField);
  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.ADD_FIELD,
      field: { ...newField, name: uniqueFieldName },
      index: insertBefore ? index : index + 1,
      currentViewId,
    })
  );
  getService('SelectionsActions').deselectAll();
};

/**
 * @deprecated Use `doInsertFieldsWithIds` instead to avoid dependency on cellSelections slice
 */
export const doInsertFields =
  (
    contentId: string,
    currentViewId: string,
    insertBefore: boolean,
    fieldName: string
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    insertFieldsInternal(
      contentId,
      currentViewId,
      insertBefore,
      fieldName,
      reader.getFieldIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doInsertFieldsWithIds =
  (
    contentId: string,
    currentViewId: string,
    insertBefore: boolean,
    fieldName: string,
    selectedFieldIds: string[]
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    insertFieldsInternal(
      contentId,
      currentViewId,
      insertBefore,
      fieldName,
      selectedFieldIds,
      dispatch,
      getState
    );
  };

/**
 * Internal helper for duplicating entire fields.
 */
const duplicateEntireFieldsInternal = (
  contentId: string,
  currentViewId: string,
  fieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>
) => {
  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLONE_FIELDS,
      fieldIds: Object.fromEntries(fieldIds.map((fieldId) => [fieldId, generateId()])),
      currentViewId,
    })
  );
};

/**
 * @deprecated Use `doDuplicateEntireFieldsWithIds` instead to avoid dependency on cellSelections slice
 */
export const doDuplicateEntireFields =
  (contentId: string, currentViewId: string): Dispatcher<void> =>
  (dispatch) => {
    const reader = getService('SelectionsReader');
    duplicateEntireFieldsInternal(
      contentId,
      currentViewId,
      reader.getSelectedFieldIds(),
      dispatch
    );
  };

export const doDuplicateEntireFieldsWithIds =
  (contentId: string, currentViewId: string, fieldIds: string[]): Dispatcher<void> =>
  (dispatch) => {
    duplicateEntireFieldsInternal(contentId, currentViewId, fieldIds, dispatch);
  };

/**
 * Internal helper for clearing entire fields.
 */
const clearEntireFieldsInternal = (
  contentId: string,
  fieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>
) => {
  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLEAR_ENTRIES,
      fieldIds,
    })
  );
};

/**
 * @deprecated Use `doClearEntireFieldsWithIds` instead to avoid dependency on cellSelections slice
 */
export const doClearEntireFields =
  (contentId: string): Dispatcher<void> =>
  (dispatch) => {
    const reader = getService('SelectionsReader');
    clearEntireFieldsInternal(contentId, reader.getSelectedFieldIds(), dispatch);
  };

export const doClearEntireFieldsWithIds =
  (contentId: string, fieldIds: string[]): Dispatcher<void> =>
  (dispatch) => {
    clearEntireFieldsInternal(contentId, fieldIds, dispatch);
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity as we migrate away from cellSelections slice dependency.
 */
const deleteFieldsInternal = (
  contentId: string,
  fieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const reliability = ReliabilityMetrics.getInstance();

  try {
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        fieldIds.forEach((fieldId) => {
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.DELETE_FIELD,
              fieldId,
            })
          );
        });
      });
    });
    reliability.success(MetricName.DATABASE_DELETE_FIELD);
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : String(e);
    reliability.failure(MetricName.DATABASE_DELETE_FIELD, {
      errorMessage,
    });
    throw e;
  }
};

/**
 * @deprecated Use `doDeleteFieldsWithIds` instead to avoid dependency on cellSelections slice
 */
export const doDeleteFields =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    deleteFieldsInternal(
      contentId,
      reader.getFieldIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doDeleteFieldsWithIds =
  (contentId: string, fieldIds: string[]): Dispatcher<void> =>
  (dispatch, getState) => {
    deleteFieldsInternal(contentId, fieldIds, dispatch, getState);
  };

/**
 * Internal helper for moving fields left or right.
 */
const moveFieldsInternal = (
  contentId: string,
  viewSettings: DatabaseViewSettings | undefined,
  moveLeft: boolean,
  fieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const databaseView = selectDatabaseByContentIdRequired(getState(), {
    contentId,
  });
  const hiddenFields = getHiddenFields(viewSettings?.fields);

  batch(() => {
    collabDatabaseBatch({ dispatch, getState }, contentId, () => {
      fieldIds.forEach((fieldId, idx) => {
        const currentIndexInFields = databaseView.fields.indexOf(fieldId);
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.REORDER_FIELD,
            fieldId,
            hiddenFields,
            newIndex: moveLeft
              ? currentIndexInFields - 1
              : currentIndexInFields + fieldIds.length - idx + 1,
          })
        );
      });
    });
  });
};

/**
 * @deprecated Use `doMoveFieldsWithIds` instead to avoid dependency on cellSelections slice
 */
export const doMoveFields =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    moveLeft: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    moveFieldsInternal(
      contentId,
      viewSettings,
      moveLeft,
      reader.getFieldIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doMoveFieldsWithIds =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    moveLeft: boolean,
    fieldIds: string[]
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    moveFieldsInternal(contentId, viewSettings, moveLeft, fieldIds, dispatch, getState);
  };

export const doSelectNewFields =
  (contentId: string, callback: () => Promise<void> | void): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    await callback();
    const newDatabase = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const newFieldIds = newDatabase.fields.filter((id) => !database.fields.includes(id));
    if (newFieldIds.length > 0) {
      batch(() => {
        getService('SelectionsActions').selectFields(newFieldIds);
      });
    }
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity as we migrate away from cellSelections slice dependency.
 */
const clearValuesInternal = (
  contentId: string,
  entryIds: string[],
  fieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const database = selectDatabaseByContentIdRequired(getState(), {
    contentId,
  });

  for (const fieldId of fieldIds) {
    const field = database.fieldsById[fieldId];
    const isPageStatus = field?.type === DatabaseFieldType.PAGE_STATUS;
    const isPageLabels = field?.type === DatabaseFieldType.PAGE_LABELS;

    if (!isPageStatus && !isPageLabels) {
      continue;
    }

    for (const entryId of entryIds) {
      const entry = getDatabaseEntryOptional(database, entryId);
      if (!entry) {
        continue;
      }

      const contentRefs = getReferencedStatusContentRefs(
        { database, field, entry },
        getState()
      );

      for (const contentRef of contentRefs) {
        if (isPageStatus) {
          applyPageStatus(contentRef, null, { getState, dispatch });
        }
        if (isPageLabels) {
          const currentLabels = getContentLabels(contentRef, {}, getState()) ?? [];
          if (currentLabels.length > 0) {
            applyPageLabels(
              contentRef,
              { add: [], remove: currentLabels },
              { getState, dispatch },
              true
            );
          }
        }
      }
    }
  }

  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLEAR_ENTRIES,
      entryIds,
      fieldIds,
    })
  );
};

export const doClearValues =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    clearValuesInternal(
      contentId,
      reader.getEntryIdsFromSelection(),
      reader.getFieldIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doClearValuesWithIds =
  (contentId: string, entryIds: string[], fieldIds: string[]): Dispatcher<void> =>
  (dispatch, getState) => {
    clearValuesInternal(contentId, entryIds, fieldIds, dispatch, getState);
  };
